import { useEffect, useState } from "react";
import { sanitizeHtml } from "../lib/sanitize";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { toast } from "sonner";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import RadarShowcase from "@/components/RadarShowcase";
import {
  Radar, ScanSearch, Sparkles, BellRing, Globe2, FileText,
  Gavel, TrendingUp, ArrowRight, Check, MapPinned, LineChart, Clock, Cpu, Zap, Layers, ShieldCheck,
  Target, BarChart3, Search, Filter, ChevronDown,
} from "lucide-react";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/c02d0d4d-4fc5-4a7f-839a-45cfd2e97cdc/images/fa2fed33a2a1733512816cd70389325e4609e0188cd8220a7287bf9097c7659f.png";
const AMBER = "#F5A623";

const HERO = "https://images.unsplash.com/photo-1600701602885-a50191d4eef2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const GOLD = "#C6A87C";

const FACT_ICONS = [Globe2, Clock, Zap, Cpu, Layers];
const BENEFIT_ICONS = [ScanSearch, Sparkles, Globe2, TrendingUp];
const FEATURE_ICONS = [Radar, LineChart, BellRing, FileText, Gavel, MapPinned];
const CHIP_ICONS = [Clock, Zap, Cpu, TrendingUp];
const STEP_ICONS = [ScanSearch, Sparkles, LineChart];

const LIVE_STATS = [];
const TESTIMONIALS = [];

const FALLBACK_PLANS = [
  { id: "starter", name: "Essentiel", price_eur: 69, price_annual_eur: 724.5, annual_full_eur: 828, annual_savings_eur: 103.5 },
  { id: "pro", name: "Premium", price_eur: 149, price_annual_eur: 1564.5, annual_full_eur: 1788, annual_savings_eur: 223.5 },
  { id: "business", name: "Expert", price_eur: 189, price_annual_eur: 1984.5, annual_full_eur: 2268, annual_savings_eur: 283.5 },
];

const eurFmt = (n, dec = 2) => Number(n || 0).toLocaleString("fr-FR", { minimumFractionDigits: dec, maximumFractionDigits: dec });

export default function Landing() {
  const { t } = useTranslation();
  const [plans, setPlans] = useState(FALLBACK_PLANS);
  const [lead, setLead] = useState({ name: "", email: "", company: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    api.get("/plans")
      .then((r) => { if (r.data?.plans?.length) setPlans(r.data.plans); })
      .catch(() => {});
  }, []);

  const planFeatures = (id, fallback) => {
    const arr = t(`landing.plans.items.${id}.features`, { returnObjects: true });
    if (Array.isArray(arr) && arr.length) return arr;
    return Array.isArray(fallback) ? fallback : [];
  };
  const planName = (id, fallback) => t(`landing.plans.items.${id}.name`, { defaultValue: "" }) || fallback || id;

  const facts = t("landing.facts.items", { returnObjects: true }) || [];
  const chips = t("landing.positioning.chips", { returnObjects: true }) || [];
  const steps = t("landing.positioning.steps", { returnObjects: true }) || [];
  const benefits = t("landing.benefits.items", { returnObjects: true }) || [];
  const features = t("landing.features.items", { returnObjects: true }) || [];

  const submitLead = async (e) => {
    e.preventDefault();
    if (!lead.name.trim() || !lead.email.trim() || !lead.phone.trim() || !lead.message.trim()) {
      toast.error(t("landing.contact.toastValidation"));
      return;
    }
    setSending(true);
    try {
      await api.post("/leads", lead);
      setSent(true);
      setLead({ name: "", email: "", company: "", phone: "", message: "" });
      toast.success(t("landing.contact.toastSuccess"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("landing.contact.toastError"));
    } finally { setSending(false); }
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-white text-[#0F172A]" data-testid="landing-page">
      {/* ===== HERO — visuel de référence (sombre + or, skyline + mappemonde) ===== */}
      <section className="relative bg-[#050506] px-3 pb-6 pt-3 sm:px-5 sm:pt-5" data-testid="landing-hero">
        <div className="relative mx-auto max-w-[1400px] overflow-hidden rounded-[26px] border border-[#F5A623]/30 bg-[#08080c] shadow-[0_0_80px_-20px_rgba(245,166,35,0.25)]">
          {/* Fond */}
          <div className="absolute inset-0">
            <img src={HERO_BG} alt="" className="h-full w-full object-cover" />
            <div className="absolute inset-0" style={{ background: "radial-gradient(120% 90% at 50% 60%, rgba(8,8,12,0.35) 0%, rgba(8,8,12,0.72) 55%, rgba(8,8,12,0.92) 100%)" }} />
          </div>

          {/* Nav */}
          <header className="relative z-20">
            <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3 px-4 py-4 sm:px-8 sm:py-6 lg:gap-10 lg:px-12 xl:px-14">
              {/* Marque : RADAR + (sous) Intelligence immobilière + (à droite) Tout-en-un */}
              <div className="flex min-w-0 shrink items-center gap-2.5 sm:gap-3.5">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#F5A623]/50 bg-[#F5A623]/10 sm:h-10 sm:w-10">
                  <Radar className="h-5 w-5" style={{ color: AMBER }} strokeWidth={2} />
                </span>
                <div className="flex min-w-0 flex-col leading-none">
                  <span className="font-display text-xl tracking-wide text-white sm:text-2xl">RADAR</span>
                  <span className="mt-1.5 hidden text-[10px] font-medium uppercase tracking-[0.2em] text-slate-400 sm:block">{t("landing.nav.tagline")}</span>
                </div>
                <span className="mx-1 hidden h-9 w-px bg-white/15 lg:block" />
                <button
                  data-testid="landing-toutenun-link"
                  onClick={() => document.getElementById("fonctionnalites")?.scrollIntoView({ behavior: "smooth", block: "start" })}
                  className="hidden whitespace-nowrap text-lg font-extrabold uppercase tracking-[0.12em] transition-transform hover:scale-[1.04] lg:block xl:text-xl"
                  style={{ color: "#3B82F6", textShadow: "0 0 18px rgba(59,130,246,0.55)" }}
                  title={t("landing.nav.features")}>
                  {t("landing.nav.allInOne")}
                </button>
              </div>
              {/* Actions — 2ᵉ ligne en mobile, sur UNE seule ligne (réparties), compactées ; alignées à droite dès sm */}
              <div className="flex w-full shrink-0 flex-nowrap items-center justify-between gap-2 sm:w-auto sm:justify-end sm:gap-3.5">
                <LanguageSwitcher variant="light" />
                <button
                  data-testid="landing-features-link"
                  onClick={() => document.getElementById("fonctionnalites")?.scrollIntoView({ behavior: "smooth", block: "start" })}
                  className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-white px-3 py-3 text-[13px] font-semibold text-[#0F172A] transition-transform hover:scale-[1.03] sm:gap-1.5 sm:px-5 sm:py-2.5 sm:text-sm">
                  <Sparkles className="hidden h-3.5 w-3.5 sm:inline-block" style={{ color: AMBER }} /> {t("landing.nav.features")}
                </button>
                <Link to="/login" data-testid="landing-login-link"
                  className="inline-flex items-center whitespace-nowrap rounded-full px-3 py-3 text-[13px] font-semibold text-[#08080c] transition-transform hover:scale-[1.03] sm:px-5 sm:py-2.5 sm:text-sm"
                  style={{ background: AMBER }}>
                  {t("landing.nav.login")}
                </Link>
              </div>
            </div>
          </header>

          {/* Contenu */}
          <div className="relative z-10 mx-auto max-w-4xl px-6 pb-8 pt-10 text-center sm:pt-16">
            <motion.div initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
              <div className="mx-auto inline-flex items-center gap-2 rounded-full border border-[#F5A623]/40 bg-[#F5A623]/10 px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.16em]" style={{ color: AMBER }}>
                <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#F5A623] opacity-60" /><span className="relative inline-flex h-2 w-2 rounded-full bg-[#F5A623]" /></span>
                {t("landing.hero.overline")}
              </div>

              <h1 className="mt-7 font-display font-semibold leading-[1.08] text-white">
                <span className="block text-3xl sm:text-4xl lg:text-[3.25rem]">{t("landing.hero.title")}</span>
                <span className="mt-4 flex items-center justify-center gap-4">
                  <span className="h-px w-10 bg-gradient-to-r from-transparent to-[#F5A623]/70 sm:w-16" />
                  <span className="text-4xl sm:text-5xl lg:text-[3.75rem]" style={{ color: AMBER, textShadow: "0 0 34px rgba(245,166,35,0.45)" }}>{t("landing.hero.titleAccent")}.</span>
                  <span className="h-px w-10 bg-gradient-to-l from-transparent to-[#F5A623]/70 sm:w-16" />
                </span>
              </h1>

              <p className="mx-auto mt-7 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">
                {t("landing.hero.desc")}
              </p>

              <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
                <Link to="/register" data-testid="landing-cta-register"
                  className="group inline-flex items-center gap-2 rounded-full px-8 py-4 text-sm font-bold text-[#08080c] transition-transform hover:scale-[1.03]"
                  style={{ background: AMBER, boxShadow: "0 14px 40px -12px rgba(245,166,35,0.6)" }}>
                  <Target className="h-4 w-4" /> {t("landing.hero.ctaLaunch")} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
                <button onClick={() => document.getElementById("fonctionnalites")?.scrollIntoView({ behavior: "smooth", block: "start" })}
                  data-testid="landing-cta-features"
                  className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-8 py-4 text-sm font-semibold text-white backdrop-blur-sm transition-colors hover:bg-white/10">
                  <BarChart3 className="h-4 w-4" style={{ color: AMBER }} /> {t("landing.hero.ctaSeeFeatures")}
                </button>
              </div>
              <p className="mt-4 text-xs font-medium text-slate-400" data-testid="landing-trial-note">{t("landing.hero.trialNote")}</p>
              <p className="mt-5 text-xs font-medium" style={{ color: AMBER }}>{t("landing.hero.startNote")}</p>
            </motion.div>
          </div>

          {/* Bandeau fonctionnalités */}
          <div className="relative z-10 border-t border-white/10 bg-black/30 backdrop-blur-sm">
            <div className="mx-auto grid max-w-5xl grid-cols-2 divide-white/10 px-4 py-5 text-center sm:grid-cols-3 lg:grid-cols-5 lg:divide-x">
              {[
                { icon: Search, label: t("landing.hero.banner1") },
                { icon: Filter, label: t("landing.hero.banner2") },
                { icon: Globe2, label: t("landing.hero.banner3") },
                { icon: BellRing, label: t("landing.hero.banner4") },
                { icon: Zap, label: t("landing.hero.banner5") },
              ].map((f, i) => (
                <div key={i} className="flex flex-col items-center gap-2 px-3 py-2">
                  <f.icon className="h-5 w-5" style={{ color: AMBER }} strokeWidth={1.8} />
                  <span className="text-xs font-medium leading-snug text-slate-300">{f.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Vitrine — Découvrez RADAR en action (Exemple de présentation) */}
      <RadarShowcase />

      {/* Faits */}
      <section className="border-b border-slate-100 bg-white py-16">
        <div className="mx-auto max-w-6xl px-6">
          <div className="max-w-2xl">
            <p className="overline" style={{ color: GOLD }}>{t("landing.facts.overline")}</p>
            <h2 className="mt-3 font-display text-3xl text-[#0F172A] sm:text-4xl">{t("landing.facts.title")}</h2>
            <p className="mt-4 text-sm text-slate-500">{t("landing.facts.subtitle")}</p>
          </div>
          <div className="mt-12 grid gap-6 sm:grid-cols-3 lg:grid-cols-5">
            {facts.map((f, i) => {
              const Icon = FACT_ICONS[i] || Layers;
              const small = false;
              return (
                <motion.div key={i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                  className="rounded-2xl border border-slate-200 p-6" data-testid={`fact-${i}`}>
                  <Icon className="h-6 w-6" style={{ color: GOLD }} strokeWidth={1.7} />
                  <p className={`mt-4 font-display text-[#0F172A] ${small ? "text-xl leading-snug" : "text-3xl leading-none"}`}>{f.value}</p>
                  <p className="mt-2 text-sm font-semibold text-[#0F172A]">{f.label}</p>
                  {f.sub && <p className="mt-1 text-xs text-slate-400">{f.sub}</p>}
                </motion.div>
              );
            })}
          </div>

          {LIVE_STATS.length > 0 && (
            <div className="mt-8 grid gap-6 sm:grid-cols-3" data-testid="live-stats">
              {LIVE_STATS.map((s) => (
                <div key={s.label} className="rounded-2xl bg-[#0F172A] p-7 text-white">
                  <p className="font-display text-4xl" style={{ color: GOLD }}>{s.value}</p>
                  <p className="mt-2 text-sm text-slate-300">{s.label}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Positionnement */}
      <section className="relative overflow-hidden bg-[#0B1220] py-28 text-white">
        <div className="pointer-events-none absolute inset-0 opacity-[0.9]" style={{ background: "radial-gradient(60% 55% at 78% 20%, rgba(198,168,124,0.16) 0%, rgba(11,18,32,0) 60%)" }} />
        <div className="pointer-events-none absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.9) 1px, transparent 1px)", backgroundSize: "26px 26px" }} />
        <div className="pointer-events-none absolute left-0 top-0 h-px w-full" style={{ background: "linear-gradient(90deg, transparent, rgba(198,168,124,0.6), transparent)" }} />

        <div className="relative mx-auto max-w-5xl px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <div className="flex items-center gap-2">
              <span className="inline-flex h-2 w-2 animate-pulse rounded-full" style={{ background: GOLD }} />
              <p className="overline" style={{ color: GOLD }}>{t("landing.positioning.overline")}</p>
            </div>

            <p className="mt-7 font-display text-3xl leading-[1.28] text-slate-200 sm:text-4xl sm:leading-[1.3]"
               dangerouslySetInnerHTML={sanitizeHtml(t("landing.positioning.statement"))} />

            <div className="mt-10 flex flex-wrap gap-3">
              {chips.map((c, i) => {
                const Icon = CHIP_ICONS[i] || Clock;
                return (
                  <motion.span key={i} initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: 0.15 + i * 0.08 }}
                    data-testid={`positioning-chip-${i}`}
                    className="inline-flex items-center gap-2 rounded-full border border-white/12 bg-white/[0.04] px-4 py-2 text-sm text-slate-200 backdrop-blur-sm">
                    <Icon className="h-4 w-4" style={{ color: GOLD }} strokeWidth={1.8} />
                    {c}
                  </motion.span>
                );
              })}
            </div>
          </motion.div>

          <div className="mt-16 grid gap-5 sm:grid-cols-3">
            {steps.map((s, i) => {
              const Icon = STEP_ICONS[i] || ScanSearch;
              return (
                <motion.div key={i} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                  className="group relative rounded-2xl border border-white/10 bg-white/[0.03] p-7 transition-colors hover:border-[#C6A87C]/40">
                  <div className="flex items-center justify-between">
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl" style={{ background: "rgba(198,168,124,0.14)" }}>
                      <Icon className="h-5 w-5" style={{ color: GOLD }} strokeWidth={1.8} />
                    </div>
                    <span className="font-display text-2xl text-white/25 transition-colors group-hover:text-[#C6A87C]/60">{`0${i + 1}`}</span>
                  </div>
                  <h3 className="mt-5 text-lg font-semibold text-white">{s.title}</h3>
                  <p className="mt-2 text-sm text-slate-400">{s.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Avantages */}
      <section className="bg-white py-24 text-[#0F172A]">
        <div className="mx-auto max-w-6xl px-6">
          <p className="overline" style={{ color: GOLD }}>{t("landing.benefits.overline")}</p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl">{t("landing.benefits.title")}</h2>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {benefits.map((b, i) => {
              const Icon = BENEFIT_ICONS[i] || ScanSearch;
              return (
                <motion.div key={i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.07 }}
                  className="rounded-2xl border border-slate-200 p-7 transition-shadow hover:shadow-md">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#0F172A]">
                    <Icon className="h-5 w-5" style={{ color: GOLD }} strokeWidth={1.8} />
                  </div>
                  <h3 className="mt-4 text-lg font-semibold">{b.title}</h3>
                  <p className="mt-2 text-sm text-slate-500">{b.text}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Fonctionnalités */}
      <section id="fonctionnalites" className="mx-auto max-w-6xl px-6 py-24">
        <p className="overline" style={{ color: GOLD }}>{t("landing.features.overline")}</p>
        <h2 className="mt-3 font-display text-3xl text-[#0F172A] sm:text-4xl">{t("landing.features.title")}</h2>
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {features.map((f, i) => {
            const Icon = FEATURE_ICONS[i] || Radar;
            return (
              <motion.div key={i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                className="rounded-2xl border border-slate-200 p-7 transition-shadow hover:shadow-md">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#0F172A]">
                  <Icon className="h-5 w-5" style={{ color: GOLD }} strokeWidth={1.8} />
                </div>
                <h3 className="mt-4 text-lg font-semibold">{f.title}</h3>
                <p className="mt-2 text-sm text-slate-500">{f.text}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Formules */}
      <section id="formules" className="bg-[#FAFAFA] py-24">
        <div className="mx-auto max-w-6xl px-6">
          <div className="text-center">
            <p className="overline" style={{ color: GOLD }}>{t("landing.plans.overline")}</p>
            <h2 className="mt-3 font-display text-3xl text-[#0F172A] sm:text-4xl">{t("landing.plans.title")}</h2>
            <p className="mx-auto mt-4 max-w-xl text-base text-slate-500">{t("landing.plans.subtitle")}</p>
          </div>
          <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {plans.map((p) => {
              const highlight = p.id === "pro";
              const name = planName(p.id, p.name);
              const feats = planFeatures(p.id, p.features);
              return (
                <div key={p.id} data-testid={`landing-plan-${p.id}`}
                  className={`relative flex flex-col rounded-2xl border bg-white p-8 ${highlight ? "border-[#C6A87C] shadow-xl lg:-translate-y-3" : "border-slate-200"}`}>
                  {highlight && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-[#C6A87C] px-4 py-1 text-xs font-bold tracking-wide text-[#0F172A]">{t("landing.plans.badge")}</span>
                  )}
                  <h3 className="text-xl font-bold tracking-tight text-[#0F172A]" style={{ fontFamily: "'Manrope', sans-serif" }}>{name}</h3>
                  <p className="mt-4" style={{ fontFamily: "'Manrope', sans-serif" }}>
                    <span className="text-4xl font-bold tabular-nums tracking-tight text-[#0F172A]" style={{ fontVariantNumeric: "lining-nums tabular-nums" }}>{p.price_eur}</span>
                    <span className="ml-0.5 text-lg font-semibold text-[#0F172A]" style={{ verticalAlign: "0.85em" }}>€</span>
                    <span className="ml-1.5 text-sm font-light text-slate-400" style={{ verticalAlign: "0.5em" }}>{t("landing.plans.perMonth")}</span>
                  </p>
                  <div className="mt-2 space-y-0.5" data-testid={`landing-plan-${p.id}-pricing`}>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-[#8a6d3b]">{t("landing.plans.commit12")}</p>
                    <p className="text-xs text-slate-500">{t("landing.plans.over12", { total: eurFmt(p.annual_full_eur ?? p.price_eur * 12) })}</p>
                    <p className="text-xs font-semibold text-emerald-600">
                      {t("landing.plans.annualSave", { annual: eurFmt(p.price_annual_eur ?? p.price_eur * 10.5), save: eurFmt(p.annual_savings_eur ?? p.price_eur * 1.5) })}
                    </p>
                  </div>
                  <ul className="mt-6 flex-1 space-y-3">
                    {feats.map((f, fi) => (
                      <li key={fi} className="flex items-start gap-2.5 text-sm text-slate-600">
                        <Check className="mt-0.5 h-4 w-4 shrink-0" style={{ color: GOLD }} /> {f}
                      </li>
                    ))}
                  </ul>
                  <Link to={`/abonnement?plan=${p.id}&billing=month`} data-testid={`landing-plan-cta-${p.id}`}
                    className={`mt-8 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold transition-transform hover:scale-[1.02] ${highlight ? "bg-[#C6A87C] text-[#0F172A]" : "bg-[#0F172A] text-white"}`}>
                    {t("landing.plans.choose")} {name} <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Témoignages */}
      {TESTIMONIALS.length > 0 && (
        <section className="mx-auto max-w-6xl px-6 py-24" data-testid="testimonials">
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {TESTIMONIALS.map((tm, i) => (
              <figure key={i} className="rounded-2xl border border-slate-200 p-7">
                <blockquote className="text-sm text-slate-600">“{tm.quote}”</blockquote>
                <figcaption className="mt-4 text-sm font-semibold text-[#0F172A]">{tm.author}<span className="ml-1 font-normal text-slate-400">· {tm.role}</span></figcaption>
              </figure>
            ))}
          </div>
        </section>
      )}

      {/* Contact */}
      <section id="contact" className="mx-auto max-w-6xl px-6 py-24" data-testid="contact-section">
        <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
          <div>
            <p className="overline" style={{ color: GOLD }}>{t("landing.contact.overline")}</p>
            <h2 className="mt-3 font-display text-3xl text-[#0F172A] sm:text-4xl">{t("landing.contact.title")}</h2>
            <p className="mt-5 text-base text-slate-600">{t("landing.contact.desc")}</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-7 sm:p-8">
            {sent ? (
              <div className="flex flex-col items-center py-10 text-center" data-testid="contact-success">
                <Check className="h-10 w-10" style={{ color: GOLD }} />
                <p className="mt-4 text-lg font-semibold text-[#0F172A]">{t("landing.contact.successTitle")}</p>
                <p className="mt-1 text-sm text-slate-500">{t("landing.contact.successText")}</p>
              </div>
            ) : (
              <form onSubmit={submitLead} className="space-y-4" data-testid="contact-form">
                <div>
                  <label className="text-sm font-medium text-slate-700">{t("landing.contact.nameLabel")}</label>
                  <input data-testid="contact-name" value={lead.name} onChange={(e) => setLead((l) => ({ ...l, name: e.target.value }))}
                    className="mt-1.5 h-11 w-full rounded-lg border border-slate-300 px-3.5 text-sm outline-none focus:border-[#C6A87C]" placeholder={t("landing.contact.namePh")} />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">{t("landing.contact.emailLabel")}</label>
                  <input data-testid="contact-email" type="email" value={lead.email} onChange={(e) => setLead((l) => ({ ...l, email: e.target.value }))}
                    className="mt-1.5 h-11 w-full rounded-lg border border-slate-300 px-3.5 text-sm outline-none focus:border-[#C6A87C]" placeholder={t("landing.contact.emailPh")} />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">{t("landing.contact.phoneLabel")}</label>
                  <input data-testid="contact-phone" type="tel" value={lead.phone} onChange={(e) => setLead((l) => ({ ...l, phone: e.target.value }))}
                    className="mt-1.5 h-11 w-full rounded-lg border border-slate-300 px-3.5 text-sm outline-none focus:border-[#C6A87C]" placeholder={t("landing.contact.phonePh")} />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">{t("landing.contact.companyLabel")}</label>
                  <input data-testid="contact-company" value={lead.company} onChange={(e) => setLead((l) => ({ ...l, company: e.target.value }))}
                    className="mt-1.5 h-11 w-full rounded-lg border border-slate-300 px-3.5 text-sm outline-none focus:border-[#C6A87C]" placeholder={t("landing.contact.companyPh")} />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">{t("landing.contact.messageLabel")}</label>
                  <textarea data-testid="contact-message" value={lead.message} onChange={(e) => setLead((l) => ({ ...l, message: e.target.value }))}
                    rows={4} className="mt-1.5 w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm outline-none focus:border-[#C6A87C]" placeholder={t("landing.contact.messagePh")} />
                </div>
                <button type="submit" disabled={sending} data-testid="contact-submit"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#0F172A] px-6 py-3 text-sm font-semibold text-white transition-transform hover:scale-[1.01] disabled:opacity-60">
                  {sending ? t("landing.contact.submitting") : t("landing.contact.submit")} <ArrowRight className="h-4 w-4" />
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* CTA final */}
      <section className="relative overflow-hidden bg-[#0F172A] py-20 text-center text-white">
        <div className="mx-auto max-w-3xl px-6">
          <h2 className="font-display text-3xl sm:text-4xl">{t("landing.ctaFinal.title")}</h2>
          <p className="mx-auto mt-4 max-w-xl text-slate-300">{t("landing.ctaFinal.desc")}</p>
          <Link to="/register" data-testid="landing-cta-final" className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#C6A87C] px-8 py-3.5 text-sm font-semibold text-[#0F172A] transition-transform hover:scale-[1.03]">
            {t("landing.ctaFinal.cta")} <ArrowRight className="h-4 w-4" />
          </Link>
          <p className="mt-4 text-xs font-medium text-slate-400" data-testid="landing-trial-note-final">{t("landing.hero.trialNote")}</p>
          <p data-testid="landing-real-badge-final" className="mt-5 text-xs font-bold uppercase tracking-[0.08em] text-[#C6A87C]">{t("trial.realBadge")}</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white py-10">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 px-6 sm:flex-row">
          <div className="flex items-center gap-2">
            <Radar className="h-5 w-5" style={{ color: GOLD }} strokeWidth={1.8} />
            <span className="font-display text-lg text-[#0F172A]">RADAR</span>
            <span className="ml-2 text-sm text-slate-400">radar-immo.fr</span>
          </div>
          <div className="flex items-center gap-5 text-sm text-slate-500">
            <LanguageSwitcher variant="dark" />
            <Link to="/login" className="hover:text-[#0F172A]">{t("landing.footer.login")}</Link>
            <Link to="/register" className="hover:text-[#0F172A]">{t("landing.footer.register")}</Link>
            <Link to="/conditions-generales" className="hover:text-[#0F172A]" data-testid="footer-cgu-link">{t("landing.footer.cgu")}</Link>
            <span className="text-slate-300">© {new Date().getFullYear()} RADAR</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
